import { useState, useMemo, useEffect, useRef } from 'react';
import { useAppContext } from '../context/AppContext';
import { fmt, fmtDate, calculateEventTotal, getPaymentStatusBadge, getPaymentsByCategory, getCategoryLabel } from '../lib/utils';
import { getPermissions } from '../lib/userSystem';
import { parseLocalDate } from '../lib/dateHelpers';
import { useNotify } from '../components/NotifyProvider';
import { FileText, Search, X, Printer, Trash2 } from 'lucide-react';

export default function Billing({ onSelectEvent }) {
  const { events, payments, currentUser, updateEvent, deletePayment } = useAppContext();
  const { notifySuccess, notifyError } = useNotify();
  const permissions = getPermissions(currentUser?.role);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedEventForInvoice, setSelectedEventForInvoice] = useState(null);

  // Calculate KPI metrics
  const billableEvents = useMemo(() => {
    return events.filter(
      (e) => ['confirmed', 'deposit_paid', 'complete'].includes(e.stage)
    );
  }, [events]);

  // calculateEventTotal already includes consumption + roomHire + packageTotal,
  // so this is the single source of truth for event revenue. Do NOT add
  // consumption a second time — that double-counts it in Total Revenue and
  // inflates Outstanding Balance by the same amount.
  const totalRevenue = useMemo(() => {
    return billableEvents.reduce((sum, e) => sum + calculateEventTotal(e), 0);
  }, [billableEvents]);

  // Consumption component — displayed separately in some UI breakdowns
  // but NOT added to the overall totalRevenue above.
  const consumptionRevenue = useMemo(() => {
    return billableEvents.reduce((sum, e) => sum + (e.consumption || 0), 0);
  }, [billableEvents]);

  // Package + room hire, excluding consumption (for the "Package vs
  // Consumption" split card).
  const packageRevenue = totalRevenue - consumptionRevenue;

  const totalPaid = useMemo(() => {
    const billableIds = new Set(billableEvents.map(e => e.id));
    return payments
      .filter((p) => billableIds.has(p.event_id))
      .reduce((sum, p) => sum + (p.amount || 0), 0);
  }, [payments, billableEvents]);

  const outstandingBalance = totalRevenue - totalPaid;

  // Group payments by category
  const paymentsByCategory = useMemo(() => {
    const cats = { deposit: 0, room_hire: 0, fb_package: 0, final_balance: 0, other: 0, package: 0 };
    payments.forEach(p => {
      const cat = p.category || 'package';
      cats[cat] = (cats[cat] || 0) + (p.amount || 0);
    });
    return cats;
  }, [payments]);

  // Forecasted vs Locked breakdown
  const { forecastedRevenue, lockedRevenue } = useMemo(() => {
    const forecasted = events
      .filter(e => ['enquiry', 'quote_sent'].includes(e.stage))
      .reduce((sum, e) => sum + calculateEventTotal(e), 0);

    const locked = events
      .filter(e => ['confirmed', 'deposit_paid', 'complete'].includes(e.stage))
      .reduce((sum, e) => sum + calculateEventTotal(e), 0);

    return { forecastedRevenue: forecasted, lockedRevenue: locked };
  }, [events]);

  // Stable invoice numbers.
  //
  // The authoritative source is `event.invoiceNumber` stored in the DB
  // (column added in migration 014 and backfilled there). The map below
  // prefers the stored value. For any billable event that still lacks a
  // stored number (edge case: a new event confirmed after the migration
  // ran), we compute the next available ERR-YYYY-NNN for its year from
  // the already-assigned numbers and persist it via updateEvent — once.
  //
  // Critically, we DO NOT recompute numbers from the current sort order
  // any more, so deleting or archiving an event can never re-shuffle the
  // invoices of the events around it.
  const invoiceNumberMap = useMemo(() => {
    const map = {};
    billableEvents.forEach((evt) => {
      if (evt.invoiceNumber) map[evt.id] = evt.invoiceNumber;
    });
    return map;
  }, [billableEvents]);

  // Persist an invoice number for any billable event that doesn't have one
  // yet. Uses a ref to ensure we only try once per event per mount.
  const pendingAssignmentRef = useRef(new Set());
  useEffect(() => {
    const needing = billableEvents.filter((e) => !e.invoiceNumber);
    if (needing.length === 0) return;

    // Determine the highest existing suffix per year, so a new assignment
    // doesn't collide with numbers already issued (including any backfilled
    // via migration 014).
    const highestByYear = {};
    events.forEach((e) => {
      const num = e.invoiceNumber;
      if (!num) return;
      const m = /^ERR-(\d{4})-(\d+)$/.exec(num);
      if (!m) return;
      const yr = parseInt(m[1], 10);
      const seq = parseInt(m[2], 10);
      if (!highestByYear[yr] || seq > highestByYear[yr]) highestByYear[yr] = seq;
    });

    needing
      .slice()
      .sort((a, b) => (a.date || '').localeCompare(b.date || '') || String(a.id).localeCompare(String(b.id)))
      .forEach((evt) => {
        if (pendingAssignmentRef.current.has(evt.id)) return;
        pendingAssignmentRef.current.add(evt.id);
        const d = parseLocalDate(evt.date) || new Date();
        const year = d.getFullYear();
        highestByYear[year] = (highestByYear[year] || 0) + 1;
        const next = `ERR-${year}-${String(highestByYear[year]).padStart(3, '0')}`;
        updateEvent(evt.id, { invoiceNumber: next }).catch((err) => {
          console.error('Failed to assign invoice number', err);
          pendingAssignmentRef.current.delete(evt.id);
        });
      });
  }, [billableEvents, events, updateEvent]);

  // Get invoiced events with details — includes consumption in total & outstanding
  const invoicedEvents = useMemo(() => {
    return billableEvents.map((event) => {
      const eventPayments = payments.filter((p) => p.event_id === event.id);
      const totalPaidForEvent = eventPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const totalValue = (event.packageTotal || 0) + (event.roomHire || 0) + (event.consumption || 0);
      const outstanding = totalValue - totalPaidForEvent;
      const statusBadge = getPaymentStatusBadge(event, eventPayments);

      return {
        ...event,
        totalValue,
        totalPaidForEvent,
        outstanding,
        statusBadge,
      };
    });
  }, [billableEvents, payments]);

  // Filter invoiced events by search
  const filteredInvoicedEvents = useMemo(() => {
    if (!searchTerm) return invoicedEvents;
    const term = searchTerm.toLowerCase();
    return invoicedEvents.filter(
      (e) =>
        e.name.toLowerCase().includes(term) ||
        e.client.toLowerCase().includes(term)
    );
  }, [invoicedEvents, searchTerm]);

  // Calculate monthly revenue (package + roomHire + consumption)
  const monthlyRevenue = useMemo(() => {
    const months = {};
    invoicedEvents.forEach((event) => {
      const date = new Date(event.date);
      const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      months[monthKey] = (months[monthKey] || 0) + (event.totalValue || 0);
    });
    return months;
  }, [invoicedEvents]);

  // Get sorted monthly data for chart
  const sortedMonths = useMemo(() => {
    return Object.entries(monthlyRevenue)
      .sort(([a], [b]) => a.localeCompare(b))
      .slice(-6); // Last 6 months
  }, [monthlyRevenue]);

  const maxMonthlyRevenue = Math.max(
    ...sortedMonths.map(([, amount]) => amount),
    1
  );

  // Get payment history sorted by date descending
  const paymentHistory = useMemo(() => {
    const withEventNames = payments.map((p) => {
      const event = events.find((e) => e.id === p.event_id);
      return {
        ...p,
        eventName: event?.name || 'Unknown Event',
      };
    });
    return withEventNames.sort((a, b) => {
      const dateA = new Date(a.payment_date);
      const dateB = new Date(b.payment_date);
      return dateB - dateA;
    });
  }, [payments, events]);

  // Delete a single payment (e.g. wrong amount or wrong event). Only
  // available to roles with delete-payment permission.
  const handleDeletePayment = async (payment) => {
    if (!payment || !payment.id) return;
    const label = payment.description
      ? `"${payment.description}" (${fmt(payment.amount)})`
      : `${fmt(payment.amount)} on ${fmtDate(payment.payment_date)}`;
    if (!window.confirm(`Delete payment ${label}?\n\nThis cannot be undone.`)) return;
    try {
      await deletePayment(payment.id);
      notifySuccess('Payment deleted');
    } catch (err) {
      notifyError('Failed to delete payment: ' + (err?.message || 'unknown error'));
    }
  };

  // Get stable invoice number. Prefer the DB-stored value (via the map
  // populated above) and fall back to a placeholder while a new event is
  // still being assigned in the background.
  const getInvoiceNumber = (event) => invoiceNumberMap[event.id] || event.invoiceNumber || 'ERR-pending';

  // Handle Generate Invoice click
  const handleGenerateInvoice = (event) => {
    setSelectedEventForInvoice(event);
  };

  // Handle Print Invoice — add class to hide everything except the invoice modal
  const handlePrintInvoice = () => {
    document.body.classList.add('printing-invoice');
    window.print();
    document.body.classList.remove('printing-invoice');
  };

  return (
    <div className="space-y-6 p-4 md:p-6 lg:p-8 bg-gray-50 min-h-screen" id="billing-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">{permissions.canViewRevenue ? 'Billing & Revenue Dashboard' : 'Billing & Invoices'}</h1>
      </div>

      {/* KPI Cards - Revenue hidden from Reception */}
      {permissions.canViewRevenue && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Package Revenue</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{fmt(packageRevenue)}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Consumption Revenue</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{fmt(consumptionRevenue)}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Total Revenue</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{fmt(totalRevenue)}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Outstanding Balance</p>
              <p className="text-2xl font-bold text-red-600 mt-2">{fmt(outstandingBalance)}</p>
            </div>
          </div>

          {/* Revenue Breakdown by Category */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Deposits Received</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{fmt(paymentsByCategory.deposit || 0)}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Room Hire Received</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{fmt(paymentsByCategory.room_hire || 0)}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">F&B/Package Received</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{fmt(paymentsByCategory.fb_package || 0)}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Final Balance Received</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{fmt(paymentsByCategory.final_balance || 0)}</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Other Payments</p>
              <p className="text-2xl font-bold text-gray-900 mt-2">{fmt(paymentsByCategory.other || 0)}</p>
            </div>
          </div>

          {/* Forecasted vs Locked Revenue */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-amber-500">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Forecasted Revenue</p>
              <p className="text-2xl font-bold text-amber-600 mt-2">{fmt(forecastedRevenue)}</p>
              <p className="text-xs text-gray-500 mt-2">Enquiries & quotes</p>
            </div>

            <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
              <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">Locked Revenue</p>
              <p className="text-2xl font-bold text-green-600 mt-2">{fmt(lockedRevenue)}</p>
              <p className="text-xs text-gray-500 mt-2">Confirmed & completed</p>
            </div>
          </div>
        </div>
      )}

      {/* Middle Section: 2 Columns */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: Invoiced Events */}
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200 p-4 md:p-6">
            <h2 className="text-lg font-bold text-gray-900">Invoiced Events</h2>
          </div>

          <div className="p-4 md:p-6">
            {/* Search Bar */}
            <div className="mb-4 relative">
              <Search size={16} className="absolute left-3 top-3 text-gray-400" />
              <input
                type="text"
                placeholder="Search by event name or client..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pl-9 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Events List */}
            <div className="space-y-3 max-h-[500px] overflow-y-auto">
              {filteredInvoicedEvents.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">No invoiced events found</p>
              ) : (
                filteredInvoicedEvents.map((event) => (
                  <div
                    key={event.id}
                    className="border border-gray-200 rounded-lg p-3 hover:bg-gray-50 transition"
                  >
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <h4
                          className="font-semibold text-sm text-blue-700 hover:text-blue-900 cursor-pointer hover:underline"
                          onClick={() => onSelectEvent?.(event)}
                          title="View full event details"
                        >
                          {event.name}
                        </h4>
                        <p className="text-xs text-gray-500">{fmtDate(event.date)}</p>
                      </div>
                      <span
                        className="text-xs px-2 py-1 rounded text-white"
                        style={{
                          backgroundColor: event.statusBadge.color,
                        }}
                      >
                        {event.statusBadge.label}
                      </span>
                    </div>

                    <div className="grid grid-cols-3 gap-2 text-xs mb-2">
                      <div>
                        <p className="text-gray-500">Total Value</p>
                        <p className="font-semibold text-gray-900">{fmt(event.totalValue)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Paid</p>
                        <p className="font-semibold text-green-600">{fmt(event.totalPaidForEvent)}</p>
                      </div>
                      <div>
                        <p className="text-gray-500">Outstanding</p>
                        <p className="font-semibold text-red-600">{fmt(event.outstanding)}</p>
                      </div>
                    </div>

                    {/* Payment breakdown by category */}
                    {event.totalPaidForEvent > 0 && (
                      <div className="mb-2 text-xs bg-gray-50 p-2 rounded">
                        {(() => {
                          const catBreakdown = getPaymentsByCategory(event, payments);
                          const categories = ['deposit', 'room_hire', 'fb_package', 'final_balance', 'other'];
                          const nonZero = categories.filter(cat => catBreakdown[cat] > 0);
                          return nonZero.length > 0 ? (
                            <p className="text-gray-600">
                              {nonZero.map(cat => `${fmt(catBreakdown[cat])} ${getCategoryLabel(cat)}`).join(', ')}
                            </p>
                          ) : null;
                        })()}
                      </div>
                    )}

                    <button
                      onClick={() => handleGenerateInvoice(event)}
                      className="w-full mt-2 text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 px-2 py-1.5 rounded flex items-center justify-center gap-1 font-medium transition"
                    >
                      <FileText size={14} /> Generate Invoice
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Right: Revenue Breakdown by Payment Category (hidden from Reception) */}
        {permissions.canViewRevenue ? (
        <div className="bg-white rounded-lg shadow">
          <div className="border-b border-gray-200 p-4 md:p-6">
            <h2 className="text-lg font-bold text-gray-900">Payments Received by Category</h2>
          </div>

          <div className="p-4 md:p-6 space-y-6">
            {/* Payment Categories Breakdown */}
            {(() => {
              const totalPaid = Object.values(paymentsByCategory).reduce((sum, v) => sum + v, 0);
              const categories = [
                { key: 'deposit', label: 'Deposits', color: 'bg-blue-500' },
                { key: 'room_hire', label: 'Room Hire', color: 'bg-purple-500' },
                { key: 'fb_package', label: 'F&B / Package', color: 'bg-amber-500' },
                { key: 'final_balance', label: 'Final Balance', color: 'bg-green-500' },
                { key: 'other', label: 'Other', color: 'bg-gray-500' },
              ];
              return categories.map(cat => (
                <div key={cat.key}>
                  <div className="flex justify-between mb-2 text-sm">
                    <span className="text-gray-700">{cat.label}</span>
                    <span className="font-semibold text-gray-900">
                      {fmt(paymentsByCategory[cat.key] || 0)} ({totalPaid > 0 ? (((paymentsByCategory[cat.key] || 0) / totalPaid) * 100).toFixed(1) : '0.0'}%)
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                    <div
                      className={`h-full ${cat.color}`}
                      style={{
                        width: totalPaid > 0 ? `${(((paymentsByCategory[cat.key] || 0) / totalPaid) * 100)}%` : '0%',
                      }}
                    />
                  </div>
                </div>
              ));
            })()}

            {/* Monthly Breakdown */}
            <div>
              <h3 className="font-semibold text-sm text-gray-900 mb-3">Monthly Revenue (Last 6 Months)</h3>
              <div className="space-y-2">
                {sortedMonths.map(([month, amount]) => {
                  const percentage =
                    maxMonthlyRevenue > 0 ? (amount / maxMonthlyRevenue) * 100 : 0;
                  const [year, monthNum] = month.split('-');
                  const monthName = new Date(year, parseInt(monthNum) - 1).toLocaleDateString('en-IE', {
                    month: 'short',
                    year: '2-digit',
                  });

                  return (
                    <div key={month}>
                      <div className="flex justify-between mb-1 text-xs">
                        <span className="text-gray-600">{monthName}</span>
                        <span className="font-semibold text-gray-900">{fmt(amount)}</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="h-full bg-green-500"
                          style={{ width: `${percentage}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
        ) : null}
      </div>

      {/* Payment History Table */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200 p-4 md:p-6">
          <h2 className="text-lg font-bold text-gray-900">Payment History</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50">
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Date</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Event</th>
                <th className="px-4 py-3 text-right font-semibold text-gray-700">Amount</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Type</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Method</th>
                <th className="px-4 py-3 text-left font-semibold text-gray-700">Reference</th>
                <th className="px-4 py-3 text-center font-semibold text-gray-700 no-print w-12"></th>
              </tr>
            </thead>
            <tbody>
              {paymentHistory.length === 0 ? (
                <tr>
                  <td colSpan="7" className="px-4 py-4 text-center text-gray-500">
                    No payments recorded
                  </td>
                </tr>
              ) : (
                paymentHistory.map((payment) => (
                  <tr key={payment.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">{fmtDate(payment.payment_date)}</td>
                    <td className="px-4 py-3">
                      <span
                        className="text-blue-700 hover:text-blue-900 cursor-pointer hover:underline"
                        onClick={() => {
                          const evt = events.find((e) => e.id === payment.event_id);
                          if (evt) onSelectEvent?.(evt);
                        }}
                      >
                        {payment.eventName}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right font-semibold text-gray-900">{fmt(payment.amount)}</td>
                    <td className="px-4 py-3 text-gray-900">{payment.type || 'Payment'}</td>
                    <td className="px-4 py-3 text-gray-900">{payment.paymentType || '-'}</td>
                    <td className="px-4 py-3 text-gray-900 text-xs">{payment.reference || '-'}</td>
                    <td className="px-4 py-3 text-center no-print">
                      <button
                        type="button"
                        onClick={() => handleDeletePayment(payment)}
                        title="Delete this payment"
                        className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50 transition"
                        aria-label="Delete payment"
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Invoice Modal */}
      {selectedEventForInvoice && (
        <div className="invoice-print-modal fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-gray-200 p-4 md:p-6 flex justify-between items-center sticky top-0 bg-white">
              <h3 className="text-lg font-bold text-gray-900">Invoice {getInvoiceNumber(selectedEventForInvoice)}</h3>
              <button
                onClick={() => setSelectedEventForInvoice(null)}
                className="text-gray-500 hover:text-gray-700 no-print"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-4 md:p-6 space-y-4">
              {/* Invoice Header */}
              <div className="border-b border-gray-200 pb-4">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900">INVOICE</h2>
                <p className="text-xs md:text-sm text-gray-600 mt-1">Invoice #: {getInvoiceNumber(selectedEventForInvoice)}</p>
                <p className="text-xs md:text-sm text-gray-600">Date: {fmtDate(new Date())}</p>
              </div>

              {/* Event Details */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Event Name</p>
                  <p className="text-sm text-gray-900">{selectedEventForInvoice.name}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Event Date</p>
                  <p className="text-sm text-gray-900">{fmtDate(selectedEventForInvoice.date)}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Client</p>
                  <p className="text-sm text-gray-900">{selectedEventForInvoice.client}</p>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase">Event Type</p>
                  <p className="text-sm text-gray-900">{selectedEventForInvoice.type || '-'}</p>
                </div>
              </div>

              {/* Amounts Table */}
              <table className="w-full text-sm border-collapse">
                <tbody>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 text-gray-700">Package Total:</td>
                    <td className="py-2 text-right font-semibold text-gray-900">{fmt(selectedEventForInvoice.packageTotal)}</td>
                  </tr>
                  {(selectedEventForInvoice.roomHire || 0) > 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="py-2 text-gray-700">Room Hire:</td>
                      <td className="py-2 text-right font-semibold text-gray-900">{fmt(selectedEventForInvoice.roomHire)}</td>
                    </tr>
                  )}
                  {(selectedEventForInvoice.consumption || 0) > 0 && (
                    <tr className="border-b border-gray-200">
                      <td className="py-2 text-gray-700">Consumption:</td>
                      <td className="py-2 text-right font-semibold text-gray-900">{fmt(selectedEventForInvoice.consumption)}</td>
                    </tr>
                  )}
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <td className="py-2 font-semibold text-gray-900">Total:</td>
                    <td className="py-2 text-right font-semibold text-gray-900">{fmt(selectedEventForInvoice.totalValue)}</td>
                  </tr>
                  <tr className="border-b border-gray-200">
                    <td className="py-2 text-gray-700">Amount Paid:</td>
                    <td className="py-2 text-right font-semibold text-green-600">{fmt(selectedEventForInvoice.totalPaidForEvent)}</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="py-2 font-semibold text-gray-900">Outstanding Balance:</td>
                    <td className="py-2 text-right font-semibold text-red-600">{fmt(selectedEventForInvoice.outstanding)}</td>
                  </tr>
                </tbody>
              </table>

              {/* Footer */}
              <div className="border-t border-gray-200 pt-4 text-xs text-gray-600">
                <p>Thank you for your business!</p>
                <p className="mt-2">For queries, please contact Errigal Country House Hotel</p>
              </div>
            </div>

            {/* Modal Actions */}
            <div className="invoice-modal-actions border-t border-gray-200 p-4 md:p-6 bg-gray-50 flex gap-2 justify-end sticky bottom-0">
              <button
                onClick={() => setSelectedEventForInvoice(null)}
                className="px-4 py-2 h-10 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                onClick={handlePrintInvoice}
                className="px-4 py-2 h-10 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 flex items-center gap-2"
              >
                <Printer size={14} /> Print Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}